import { AbstractClient, CompositionClient } from "@/app/client";
import {
  SMSChatManagement,
  SMSChatManagementCreate,
  SMSChatManagementUpdate,
  SMSChatManagementWithShares,
} from "@/types/sms-management";

export default class APISMSManagementSub extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  // Get all chats for management for a specific account
  getManagementChats = async (
    accountId: string,
  ): Promise<SMSChatManagement[]> => {
    let res = await this._fetch({
      url: `/sms-management/${accountId}/chats`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    const contentType = res.headers.get("content-type");
    if (contentType?.includes("application/json")) return res.json();
    throw new Error(`Bad content type: ${contentType || "unknown"}`);
  };

  // Get all chats for management including shares (single aggregated call)
  getManagementChatsWithShares = async (
    accountId: string,
  ): Promise<SMSChatManagementWithShares[]> => {
    let res = await this._fetch({
      url: `/sms-management/${accountId}/chats-with-shares`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    const contentType = res.headers.get("content-type");
    if (contentType?.includes("application/json")) return res.json();
    throw new Error(`Bad content type: ${contentType || "unknown"}`);
  };

  // Create a new chat proactively
  createManagementChat = async (
    accountId: string,
    body: SMSChatManagementCreate,
  ): Promise<SMSChatManagement> => {
    let res = await this._fetch({
      url: `/sms-management/${accountId}/chats`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    const contentType = res.headers.get("content-type");
    if (contentType?.includes("application/json")) return res.json();
    throw new Error(`Bad content type: ${contentType || "unknown"}`);
  };

  // Update a chat in management interface
  updateManagementChat = async (
    chatId: string,
    body: SMSChatManagementUpdate,
  ): Promise<SMSChatManagement> => {
    let res = await this._fetch({
      url: `/sms-management/chats/${chatId}`,
      method: "PATCH",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    const contentType = res.headers.get("content-type");
    if (contentType?.includes("application/json")) return res.json();
    throw new Error(`Bad content type: ${contentType || "unknown"}`);
  };

  // Delete a chat permanently
  deleteManagementChat = async (
    chatId: string,
  ): Promise<{ success: boolean; message: string }> => {
    let res = await this._fetch({
      url: `/sms-management/chats/${chatId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    const contentType = res.headers.get("content-type");
    if (contentType?.includes("application/json")) return res.json();
    throw new Error(`Bad content type: ${contentType || "unknown"}`);
  };

  // Disable chat with cascade (delete messages and shares)
  disableChatCascade = async (
    chatId: string,
  ): Promise<{ success: boolean; message: string }> => {
    let res = await this._fetch({
      url: `/sms-chats/${chatId}/disable`,
      method: "PUT",
      auth: ["OAuth2PasswordBearer"],
    });
    const contentType = res.headers.get("content-type");
    if (contentType?.includes("application/json")) return res.json();
    throw new Error(`Bad content type: ${contentType || "unknown"}`);
  };

  // Batch update multiple chats in a single request
  batchUpdate = async (
    updates: Array<{
      chat_id: string;
      enabled?: boolean;
      display_name?: string;
      partner_id?: string | null;
      ai_context?: string;
    }>,
  ): Promise<{
    updated: number;
    results: Array<{ chat_id: string; success: boolean }>;
  }> => {
    let res = await this._fetch({
      url: `/sms-management/batch-update`,
      method: "PATCH",
      body: updates,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    const contentType = res.headers.get("content-type");
    if (contentType?.includes("application/json")) return res.json();
    throw new Error(`Bad content type: ${contentType || "unknown"}`);
  };

  // Batch delete multiple chats
  batchDelete = async (
    chatIds: string[],
  ): Promise<{
    deleted_count: number;
    deleted: string[];
    errors: Array<{ chat_id: string; error: string }>;
  }> => {
    let res = await this._fetch({
      url: `/sms-management/batch-delete`,
      method: "POST",
      body: chatIds,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    const contentType = res.headers.get("content-type");
    if (contentType?.includes("application/json")) return res.json();
    throw new Error(`Bad content type: ${contentType || "unknown"}`);
  };

  // Alias methods for shorter names (used by hooks)
  getChats = this.getManagementChats;
  createChat = this.createManagementChat;
  updateChat = this.updateManagementChat;
  deleteChat = this.deleteManagementChat;
  getChatsWithShares = this.getManagementChatsWithShares;
}
